const OIDC_AUTHORITY = import.meta.env.VITE_OIDC_AUTHORITY;
const OIDC_CLIENT_ID = import.meta.env.VITE_OIDC_CLIENT_ID;
const REFRESH_TOKEN_STORAGE_KEY = "refresh_token";
const ACCESS_TOKEN_STORAGE_KEY = "access_token";
const REFRESH_TOKEN_EXPIRY_STORAGE_KEY = "refresh_token_exp";
const TOKEN_REFRESH_LEAD_TIME = 5 * 60 * 1000;
const TOKEN_ENDPOINT = `${OIDC_AUTHORITY}/protocol/openid-connect/token`;

let refreshInFlight: Promise<RefreshTokenResponse> | null = null;

const getExpiryFromToken = (token: string): number | undefined => {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return undefined;
    }

    const decodedPayload = globalThis.atob(
      payload.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(payload.length / 4) * 4, "="),
    );
    const expiry = Number((JSON.parse(decodedPayload) as { exp?: number | string }).exp);
    return Number.isFinite(expiry) ? expiry : undefined;
  } catch {
    return undefined;
  }
};

export interface RefreshTokenResponse {
  access_token: string;
  token_type: string;
  expires_in?: number;
  refresh_expires_in?: number;
  refresh_token: string;
  scope?: string;
  [key: string]: unknown;
}

export async function refreshToken(): Promise<RefreshTokenResponse> {
  const storedRefreshToken = sessionStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
  const clientId = OIDC_CLIENT_ID;

  if (!storedRefreshToken) {
    throw new Error("No refresh token found in session storage");
  }

  if (!clientId?.trim()) {
    throw new Error("Missing VITE_OIDC_CLIENT_ID environment variable");
  }

  const payload = new URLSearchParams({
    refresh_token: storedRefreshToken,
    client_id: clientId.trim(),
    grant_type: "refresh_token",
  });

  const response = await fetch(TOKEN_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: payload.toString(),
  });

  if (!response.ok) {
    let providerError = "";
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      providerError = [errorBody.error, errorBody.error_description]
        .filter(Boolean)
        .join(": ");
    } catch {
      providerError = await response.text().catch(() => "");
    }

    throw new Error(
      `Token refresh failed: ${response.status} ${response.statusText}${providerError ? ` - ${providerError}` : ""}`,
    );
  }

  const newToken = (await response.json()) as RefreshTokenResponse;

  sessionStorage.setItem(ACCESS_TOKEN_STORAGE_KEY, newToken.access_token);
  sessionStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, newToken.refresh_token);

  const expiry = getExpiryFromToken(newToken.refresh_token);
  if (expiry !== undefined) {
    sessionStorage.setItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY, String(expiry));
  }
  return newToken;
}



export async function verifyToken(): Promise<string | null> {
  const accessToken = sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (!accessToken) {
    return null;
  }

  const expiry = Number(sessionStorage.getItem(REFRESH_TOKEN_EXPIRY_STORAGE_KEY));
  if (!Number.isFinite(expiry)) {
    throw new Error("Refresh token expiry is missing or invalid");
  }

  const currentTime = Date.now();
  const expireEpoch = expiry * 1000;
  const isTokenExpired = expireEpoch - currentTime <= TOKEN_REFRESH_LEAD_TIME;


  if (isTokenExpired) {
    refreshInFlight ??= refreshToken().finally(() => {
      refreshInFlight = null;
    });
    const refreshedToken = await refreshInFlight;
    return refreshedToken.access_token;
  }

  return accessToken;
}

export async function getToken(): Promise<string | null> {
  return sessionStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
}


export default refreshToken;